import { defineStore } from 'pinia';
import type Integration from '@/api/model/Integration';

import useIntegrationService from '@/api/IntegrationService';
import { Organisation } from '@/api/model/Organisation';
import type { StoreIntegrationRequest } from '@/api/types/Integrations';

interface IntegrationState {
    integrations: Integration[];
    loading: boolean;
}

const integrationService = useIntegrationService();

export const useIntegrationStore = defineStore('integration', {
    state: (): IntegrationState => ({
        integrations: [],
        loading: false,
    }),
    getters: {},
    actions: {
        fetch(): Promise<Integration[]> {
            if (this.loading || this.integrations.length > 0) {
                return Promise.resolve([]);
            }
            return this.refresh(new Organisation());
        },
        refresh(organisation: Organisation): Promise<Integration[]> {
            this.loading = true;
            return integrationService
                .getAll(organisation)
                .then((integrations) => {
                    return (this.integrations = integrations);
                })
                .catch((error) => {
                    this.integrations = [];
                    return Promise.reject(error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        store(organisation: Organisation, data: StoreIntegrationRequest): Promise<Integration> {
            return integrationService.create(organisation, data).then((response) => {
                this.integrations = [...this.integrations, response];
                return response;
            });
        },
        update(organisation: Organisation, integration: Integration): Promise<Integration> {
            return integrationService.update(organisation, integration).then((response) => {
                const index = this.integrations.findIndex((rs) => rs.uuid === response.uuid);
                if (index !== -1) {
                    this.integrations.splice(index, 1, response);
                }
                return response;
            });
        },
        async delete(organisation: Organisation, uuid: string): Promise<void> {
            await integrationService.delete(organisation, uuid);
            this.integrations = this.integrations.filter((rs) => rs.uuid !== uuid);
        },
    },
});
